import type { LucideIcon } from 'lucide-react';
import { MenuLinha, type AcaoLinha } from './menu-linha';

/**
 * As ações de uma linha, VISÍVEIS — só o ícone.
 *
 * Substituiu o menu "⋯" nos cadastros, e a razão é de uso: o menu esconde
 * atrás de um clique as duas coisas que a pessoa mais faz na lista. Quem não
 * sabe que o "⋯" existe não descobre que dá para editar dali — e passa a abrir
 * o registro só para desativá-lo.
 *
 * O menu não morreu: acima de três ações ele volta, porque aí a coluna viraria
 * uma barra de ferramentas e a informação da linha encolheria.
 *
 * **O texto vem no `title`, e não num balão desenhado.** A tabela rola na
 * horizontal, e `overflow-x: auto` faz o `overflow-y` computar como `auto`
 * junto — regra do CSS, não escolha nossa. Um balão posicionado acima do botão
 * seria cortado na borda da linha, e o cortado seria justamente a explicação.
 * O `title` é do navegador: aparece por cima de tudo, sem recorte, e o
 * `aria-label` garante que o leitor de tela ouça "Editar" e não "botão".
 */
export function AcoesLinha({ acoes }: { acoes: readonly AcaoLinha[] }) {
  if (acoes.length === 0) return null;
  if (acoes.length > 3) return <MenuLinha acoes={acoes} />;

  return (
    <div className="flex items-center justify-end gap-0.5">
      {acoes.map((acao) => (
        <BotaoAcao key={acao.rotulo} {...acao} />
      ))}
    </div>
  );
}

function BotaoAcao({
  rotulo,
  icone: Icone,
  aoEscolher,
  destrutiva,
}: {
  rotulo: string;
  icone: LucideIcon;
  aoEscolher: () => void;
  destrutiva?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={rotulo}
      title={rotulo}
      onClick={(evento) => {
        // A linha inteira é clicável e abre o registro. Sem parar aqui, clicar
        // em "Desativar" desativaria E navegaria — e a pessoa acabaria num
        // formulário que ela não pediu, achando que errou o alvo.
        evento.stopPropagation();
        aoEscolher();
      }}
      className={`inline-flex size-8 items-center justify-center rounded-lg transition-all duration-150 hover:scale-110 ${
        destrutiva
          ? 'text-tinta-500 hover:bg-erro-600/10 hover:text-erro-600'
          : 'text-tinta-500 hover:bg-marca-100 hover:text-marca-600'
      }`}
    >
      <Icone className="size-4 shrink-0" aria-hidden />
    </button>
  );
}
